/**
 * EPG Pipedrive Field Mapping Utilities
 *
 * Maps Pipedrive custom field hashes to EPG categories and months.
 * Extracts event data from organization objects for grid display.
 *
 * Created: 2025-12-16
 * Updated: 2025-12-19 - Phase 2: Integrated field_definitions API
 * Phase: Field Management Integration - Phase 2
 */

import type { EPGOrganization, EPGEventsByMonth, EventsByMonth } from '@shared/types/epg';
import { useFieldDefinitionsStore } from '@shared/store/fieldDefinitionsStore';

/**
 * Pipedrive field hash keys for Events category (12 months)
 * From backend: epg_service.py EVENT_FIELDS
 *
 * NOTE: These are kept as fallback constants for backward compatibility.
 * Use getEPGFieldMappings() hook for dynamic lookups from field_definitions API.
 */
export const EVENT_FIELD_HASHES: Record<string, string> = {
  JAN: 'bc5b330241a761e0411b44ab3b4f24211f69929d',
  FEB: 'a38db5e22740d9d41f3d61efa2a688c31f022a54',
  MAR: '2c8cee9ee8fa3c37692167625cb87a6a319cfa1a',
  APR: '7cf8b76eede651a0ef12129cb92b441adc193ade',
  MAY: '62d4f2f200b94db158b27b3d906c0c87cb24d7b9',
  JUN: '1d6af4f65d688bbe3cce8d631124074ffbf735b4',
  JUL: '9c06f44156944ebfbb0423ab273ddaf375afeb96',
  AUG: '7c7cc016821093372b653109760842a296f04c27',
  SEP: '5bdf515432be08aba9b27ec04837e0a77820bcc9',
  OCT: '174f11960e74b9f09403eafacc9b27de710ca92c',
  NOV: 'bb0b3c8ed12a1bf41bfe0e0bbd8d61b56176e4d9',
  DEC: '356c2aab8cf442ba0f08a3d73f1e605fd247ea6b',
};

/**
 * Pipedrive field hash keys for Calls & Emails category (12 months)
 * From backend: epg_service.py CALLS_EMAILS_FIELDS
 *
 * NOTE: These are kept as fallback constants for backward compatibility.
 */
export const CALLS_EMAILS_FIELD_HASHES: Record<string, string> = {
  JAN: 'e8b3880a0788d5ace62c8eac813d4c2c768f2792',
  FEB: '99eb646e6fbe4f4470119844fc4a0eb463e6d5b5',
  MAR: '4757298e70f331900c931a4cb900f1735ca72cc1',
  APR: '7cb5ab93e260169f069c4bffc5b0261028262b97',
  MAY: 'f09bfc82d89af86da129f1cc14bc1129f78f8b09',
  JUN: '8b4d7e54db4c45f6a42ad5fb3aaf757a71d06481',
  JUL: '63c5a4a2114e16e146306b70c16d7734f64e863a',
  AUG: 'f14e258445897486bb811fa4a23e2053dd3f0ebe',
  SEP: '31db045b4666b374999758b0fdc855fd04959140',
  OCT: 'e33feb84aca198ccf5ec1c613b28a6304b3a4978',
  NOV: '3d71c895df6f6d1f7674ffbb6dda2d3810e20d21',
  DEC: '5459f90462eaa7847244fa9d976b88a0385eb9b9',
};

/**
 * Pipedrive field hash keys for Drops category (12 months)
 * From backend: epg_service.py DROPS_FIELDS
 *
 * NOTE: These are kept as fallback constants for backward compatibility.
 */
export const DROPS_FIELD_HASHES: Record<string, string> = {
  JAN: 'a201d845a71bdaaa41eaea446d82bac03498bb24',
  FEB: 'b3e32f5d513f4c6925fc3e1c8770d0d1ff801d02',
  MAR: '4cb0811e8837fb92c4890e4ca855d3a33581ec5d',
  APR: 'b8d8e33aadc84b2480e7cf128e37685e53fb8a7e',
  MAY: '8f76e39f3d2e6f1ab0d160b01e002dd8995970c8',
  JUN: '9d669e5ad42edbda29268b1ca70b8fe9b6685c1a',
  JUL: 'c484f98ecdb6673a0d6390236e906a050f80c7b7',
  AUG: '195f15bbfc17b1ab2877a901e4c9b2e487d5e0b8',
  SEP: '8e3af2ac0ed09c19266334512f609e6caf8210d1',
  OCT: '43b3674bcd4f5d0e5906f07ce4914766330d60b4',
  NOV: 'd9799d6e2915b7d64b43b692cffdb7289c3473a8',
  DEC: '1d60e809846499f6676849199a2d2a091ef1ce8d',
};

/**
 * Dynamic EPG Field Mappings Hook
 *
 * Fetches field definitions from the shared fieldDefinitionsStore and returns
 * field hashes for EPG categories with backward compatibility fallback.
 *
 * Usage:
 * ```typescript
 * const { getFieldByHash, eventHashes, callsHashes, dropsHashes } = useEPGFieldMappings();
 *
 * // Get field metadata by hash
 * const janEventsField = getFieldByHash(eventHashes.JAN);
 * console.log(janEventsField?.field_name); // "Events - January"
 * ```
 *
 * @returns Object with field mappings and helper functions
 */
export function useEPGFieldMappings() {
  const { fields, getFieldByHash } = useFieldDefinitionsStore();

  // Helper function to build month mappings from field_definitions
  const buildMonthMappings = (fallbackHashes: Record<string, string>) => {
    const mappings: Record<string, string> = {};

    Object.keys(fallbackHashes).forEach((month) => {
      const hash = fallbackHashes[month];
      // Check if field exists in field_definitions
      const field = getFieldByHash(hash);

      // Use hash from field_definitions if found, otherwise fall back to hardcoded
      mappings[month] = field?.field_key || hash;
    });

    return mappings;
  };

  return {
    // Field mappings (dynamic with fallback)
    eventHashes: buildMonthMappings(EVENT_FIELD_HASHES),
    callsHashes: buildMonthMappings(CALLS_EMAILS_FIELD_HASHES),
    dropsHashes: buildMonthMappings(DROPS_FIELD_HASHES),

    // Helper function to get field metadata by hash
    getFieldByHash,

    // Total fields loaded (for debugging)
    totalFieldsLoaded: fields.length,
  };
}

/**
 * All month codes in order
 */
const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/**
 * Parse Pipedrive field value to array of event IDs
 *
 * Pipedrive stores multi-select values as:
 * - Comma-separated string: "909,911,912"
 * - Array: [909, 911, 912]
 * - Single value: 909 or "909"
 */
function parseFieldValue(value: unknown): number[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? parseInt(v, 10) : v)).filter((v) => !isNaN(v));
  }

  if (typeof value === 'string') {
    if (value.includes(',')) {
      return value
        .split(',')
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => !isNaN(v));
    }
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? [] : [parsed];
  }

  if (typeof value === 'number') {
    return [value];
  }

  return [];
}

/**
 * Extract events from organization data using Pipedrive field hashes
 *
 * Transforms raw Pipedrive organization data into the EPGEventsByMonth format
 * expected by the grid components.
 *
 * NOTE: Labels will be empty here - they need to be populated from field definitions
 * in the component layer where field definitions are available.
 *
 * @param organization - Organization object with Pipedrive field hash values
 * @returns EPGEventsByMonth record with events separated by category
 */
export function extractEventsFromOrganization(
  organization: EPGOrganization & Record<string, unknown>
): EPGEventsByMonth {
  const eventsByMonth: EPGEventsByMonth = {};

  // Debug: Check if field hashes are present in the organization object
  const hasFieldHashes = Object.keys(organization).some(key => key.length === 40);
  if (!hasFieldHashes) {
    console.warn('[EPG] Organization missing Pipedrive field hashes. Grid cells will show 0 events.', {
      orgId: organization.id,
      orgName: organization.name,
      keys: Object.keys(organization)
    });
  }

  for (const monthCode of MONTH_CODES) {
    // Get event IDs for each category from the organization's Pipedrive fields
    const eventsFieldKey = EVENT_FIELD_HASHES[monthCode];
    const callsFieldKey = CALLS_EMAILS_FIELD_HASHES[monthCode];
    const dropsFieldKey = DROPS_FIELD_HASHES[monthCode];

    const eventsIds = parseFieldValue(organization[eventsFieldKey]);
    const callsIds = parseFieldValue(organization[callsFieldKey]);
    const dropsIds = parseFieldValue(organization[dropsFieldKey]);

    // All IDs combined for backward compatibility
    const allIds = [...eventsIds, ...callsIds, ...dropsIds];

    // Create EventsByMonth structure
    // NOTE: Labels are left empty - they will be resolved from field definitions
    // in MonthCategoryCell component where availableOptions are loaded
    const monthData: EventsByMonth = {
      field_key: eventsFieldKey, // Primary field key (Events)
      selected_ids: allIds,
      selected_labels: [], // Empty - will be resolved from field definitions in UI
      // Category-specific arrays for proper separation
      events_selected_ids: eventsIds,
      calls_emails_selected_ids: callsIds,
      drops_selected_ids: dropsIds,
    };

    eventsByMonth[monthCode] = monthData;
  }

  return eventsByMonth;
}

/**
 * Check if organization has any events assigned
 */
export function hasAnyEvents(organization: EPGOrganization & Record<string, unknown>): boolean {
  for (const monthCode of MONTH_CODES) {
    const eventsIds = parseFieldValue(organization[EVENT_FIELD_HASHES[monthCode]]);
    const callsIds = parseFieldValue(organization[CALLS_EMAILS_FIELD_HASHES[monthCode]]);
    const dropsIds = parseFieldValue(organization[DROPS_FIELD_HASHES[monthCode]]);

    if (eventsIds.length > 0 || callsIds.length > 0 || dropsIds.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Get total event count for organization
 */
export function getTotalEventCount(organization: EPGOrganization & Record<string, unknown>): number {
  let count = 0;
  for (const monthCode of MONTH_CODES) {
    count += parseFieldValue(organization[EVENT_FIELD_HASHES[monthCode]]).length;
    count += parseFieldValue(organization[CALLS_EMAILS_FIELD_HASHES[monthCode]]).length;
    count += parseFieldValue(organization[DROPS_FIELD_HASHES[monthCode]]).length;
  }
  return count;
}
